"use client"

import { useInView } from "react-intersection-observer"
import { useState, useEffect } from "react"
import { TrendingUp, Sprout, LineChart, Globe, ArrowRight, Send, ArrowUp, Home } from "lucide-react"
import "../assets/css/investment.css" 
import ChilliImg from "../assets/images/invest-chili.jpeg"
import InvestImg from  "../assets/images/invest/invest.png"
import FundImg from "../assets/images/invest/fund.jpg"
import AgritechImg from "../assets/images/invest/agri-tech.jpg"

export default function InvestmentOpportunities() {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  // const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle")
  const [showBackToTop, setShowBackToTop] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      setShowBackToTop(scrollTop > 300)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const goToHome = () => {
    window.location.href = "/"
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    })
  }

  // Future form handling - currently form is informational only
  // const investmentRanges = [
  //   "Under $50,000",
  //   "$50,000 - $100,000",
  //   "$100,000 - $250,000",
  //   "$250,000 - $500,000",
  //   "$500,000 - $1,000,000",
  //   "Over $1,000,000",
  // ]

  // const validateForm = () => {
  //   const newErrors: Record<string, string> = {}

  //   if (!formData.name.trim()) {
  //     newErrors.name = "Name is required"
  //   }

  //   if (!formData.email.trim()) {
  //     newErrors.email = "Email is required"
  //   } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
  //     newErrors.email = "Email is invalid"
  //   }

  //   if (!formData.investmentRange) {
  //     newErrors.investmentRange = "Please select an investment range"
  //   }

  //   setErrors(newErrors)
  //   return Object.keys(newErrors).length === 0
  // }

  // Future form handling - currently form is informational only
  // const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
  //   const { name, value } = e.target
  //   setFormData((prev) => ({
  //     ...prev,
  //     [name]: value,
  //   }))

  //   // Clear error when user types
  //   if (errors[name]) {
  //     setErrors((prev) => ({
  //       ...prev,
  //       [name]: "",
  //     }))
  //   }
  // }

  // const handleSubmit = async (e: React.FormEvent) => {
  //   e.preventDefault()

  //   if (!validateForm()) {
  //     return
  //   }

  //   setFormStatus("submitting")

  //   try {
  //     // Simulate API call
  //     await new Promise((resolve) => setTimeout(resolve, 1500))

  //     // Reset form
  //     setFormData({
  //       name: "",
  //       email: "",
  //       company: "",
  //       investmentRange: "",
  //       message: "",
  //     })

  //     setFormStatus("success")

  //     // Reset success message after 5 seconds
  //     setTimeout(() => {
  //       setFormStatus("idle")
  //     }, 5000)
  //   } catch (error) {
  //     console.error("Error submitting form:", error)
  //     setFormStatus("error")
  //   }
  // }

  const investmentHighlights = [
    {
      icon: <TrendingUp className="highlight-icon" />,
      title: "High-Demand Niche Crops",
      description:
        "We focus on Scotch Bonnet dark green chillies, prized for their flavor, heat, and export appeal. Our crop selection is market-driven and adaptable.",
    },
    {
      icon: <Sprout className="highlight-icon" />,
      title: "Scalable Operations",
      description: "With 5 acres currently under cultivation in Kurunegala, our model is built for scalability—from expanding land to growing plant and grow bag sales.",
    },
    {
      icon: <LineChart className="highlight-icon" />,
      title: "Vertical Integration",
      description: "From seedling production to final harvest, we manage the full lifecycle, ensuring quality, traceability, and efficiency.",
    },
    {
      icon: <Globe className="highlight-icon" />,
      title: "Market Expansion",
      description:
        "Early traction in fresh produce sales, retailing plants & grow bags, and discussions for export opportunities to premium markets.",
    },
  ]

  const investmentOpportunities = [
    {
      title: "Expansion to 5–10 Acres of High-Value Crops",
      description:
        "Scale our cultivation area to increase production capacity of premium Scotch Bonnet chillies and other specialty crops.",
      funding: "$150,000 - $250,000",
      roi: "25-30% annually",
      timeline: "12-18 months",
    },
    {
      title: "R&D for Improved Seed Stock & Organic Pest Management",
      description:
        "Develop superior seed varieties and implement advanced organic pest management systems for higher yields and quality.",
      funding: "$100,000 - $150,000",
      roi: "20-25% annually",
      timeline: "18-24 months",
    },
    {
      title: "Facility Upgrades & Export Infrastructure",
      description: "Build processing, packing, and storage facilities along with distribution and e-commerce infrastructure for export market entry.",
      funding: "$200,000 - $350,000",
      roi: "22-28% annually",
      timeline: "18-30 months",
    },
  ]

  return (
    <section id="investments" ref={ref} className="investment-section">
      <div className="investments-container">
        <div className="section-header">
          <h2 className={`section-title ${inView ? "visible" : ""}`}>Partner With Us</h2>
          <div className={`section-divider ${inView ? "visible" : ""}`}></div>
          <p className={`section-tagline ${inView ? "visible" : ""}`}>Grow the Future of Agriculture</p>
        </div>

        <div className="investment-intro">
          <div className={`intro-content ${inView ? "visible" : ""}`}>
            <h3 className="intro-heading">Pioneering Specialty Agriculture in Sri Lanka</h3>
            <p className="intro-text">
              At Green Beat Ceylon, we are redefining Sri Lanka's agricultural landscape through high-value, specialty crops—starting with Scotch Bonnet chillies and scalable, sustainable practices. With strong demand, proven early-stage success, and expansion-ready infrastructure, we're now opening doors to strategic investment partnerships.
            </p>
            <p className="intro-text">
              <strong>We're inviting forward-thinking investors to be part of our journey towards transforming agriculture in Sri Lanka.</strong>
            </p>
          </div>
          <div className={`intro-image ${inView ? "visible" : ""}`}>
            <img
              src={ChilliImg}
              alt="Green Beat Ceylon Farm"
              width={600}
              height={400}
              className="image"
            />
            <div className="image-overlay">
              <div className="overlay-content">
                {/* <Award className="award-icon" /> */}
                {/* <p>Grow with us</p> */}
              </div>
            </div>
          </div>
        </div>

        <div className={`investment-highlights ${inView ? "visible" : ""}`}>
          <h3 className={`section-subtitle ${inView ? "visible" : ""}`}>Why Invest in Green Beat Ceylon?</h3>
          <div className="highlights-grid">
            {investmentHighlights.map((highlight, index) => (
              <div key={index} className="highlight-card" style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="highlight-icon-container">{highlight.icon}</div>
                <h4 className="highlight-title">{highlight.title}</h4>
                <p className="highlight-description">{highlight.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className={`investment-opportunities ${inView ? "visible" : ""}`}>
          <h3 className={`section-subtitle ${inView ? "visible" : ""}`}>Our Growth Roadmap</h3>
          <h3 className="intro-heading" >We are seeking investment to accelerate:</h3>
          <div className="opportunities-grid">
            {investmentOpportunities.map((opportunity, index) => (
              <div key={index} className="opportunity-card" style={{ animationDelay: `${index * 0.15}s` }}>
                <h4 className="opportunity-title">{opportunity.title}</h4>
                <p className="opportunity-description">{opportunity.description}</p>
                {/* <div className="opportunity-details">
                  <div className="detail-item">
                    <span className="detail-label">Funding Needed:</span>
                    <span className="detail-value">{opportunity.funding}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Expected ROI:</span>
                    <span className="detail-value">{opportunity.roi}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">ROI Timeline:</span>
                    <span className="detail-value">{opportunity.timeline}</span>
                  </div>
                </div> */}
                {/* <button className="opportunity-button">
                  Learn More <ArrowRight className="button-icon" />
                </button> */}
              </div>
            ))}
          </div>
        </div>

        {/* <div className={`investment-approach ${inView ? "visible" : ""}`}>
          <div className="approach-content">
            <h3 className={`section-subtitle ${inView ? "visible" : ""}`}>Our Investment Approach</h3>
            <p className="approach-text">
              We believe in transparent, mutually beneficial partnerships that drive sustainable growth. Our approach
              combines agricultural expertise with business acumen to deliver strong returns while creating positive
              environmental and social impact in Sri Lanka's agricultural sector.
            </p>
            <ul className="approach-list">
              <li className="approach-item">
                <CheckCircle className="check-icon" />
                <span>5 acres currently under cultivation in Kurunegala with proven results</span>
              </li>
              <li className="approach-item">
                <CheckCircle className="check-icon" />
                <span>Full lifecycle management from seedling to harvest ensuring quality control</span>
              </li>
              <li className="approach-item">
                <CheckCircle className="check-icon" />
                <span>Market-driven crop selection focused on high-value specialty products</span>
              </li>
              <li className="approach-item">
                <CheckCircle className="check-icon" />
                <span>Strategic expansion plan targeting Middle East, EU, and regional export markets</span>
              </li>
            </ul>
          </div>
          <div className="approach-image">
            <div className="stats-container">
              <div className="stat-item">
                <h4 className="stat-value">5</h4>
                <p className="stat-label">Acres Under Cultivation</p>
              </div>
              <div className="stat-item">
                <h4 className="stat-value">3+</h4>
                <p className="stat-label">Years of Operations</p>
              </div>
              <div className="stat-item">
                <h4 className="stat-value">5-10</h4>
                <p className="stat-label">Target Expansion (Acres)</p>
              </div>
              <div className="stat-item">
                <h4 className="stat-value">100%</h4>
                <p className="stat-label">Specialty Crop Focus</p>
              </div>
            </div>
          </div>
        </div> */}

        <div className={`lets-grow-together ${inView ? "visible" : ""}`}>
          <div className="grow-content">
            <h3 className="grow-title">Partner With Us</h3>
            <p className="grow-text">
              We welcome strategic partners who believe in the power of agriculture to drive economic transformation and rural development in Sri Lanka. Whether you bring capital, expertise, or a shared vision for sustainable farming — we'd love to connect.
            </p>
            <div className="investor-types">
              <div className="investor-type">
                <img src={InvestImg} alt="Equity Investors" className="investor-icon" />
                <h4>Equity Investors</h4>
                <p>Investors interested in sustainable agribusiness with proven growth potential and market traction</p>
              </div>
              <div className="investor-type">
                <img src={FundImg} alt="Impact Funds" className="investor-icon" />
                <h4>Impact Funds</h4>
                <p>Funds focused on rural development, food security, and sustainable agricultural practices</p>
              </div>
              <div className="investor-type">
                <img src={AgritechImg} alt="Agri-Tech Partners" className="investor-icon" />
                <h4>Agri-Tech Partners</h4>
                <p>Partners interested in precision farming, automation, and value-addition technologies</p>
              </div>
            </div>
          </div>
        </div>

        <div className={`investment-contact ${inView ? "visible" : ""}`}>
          <div className="contact-header">
            <h3 className="contact-title">Let's Grow Together</h3>
            <p className="contact-subtitle">
              If you are a forward-thinking investor with a passion for agriculture, sustainability, or emerging markets — we would love to talk.
            </p>
          </div>

          <div className="contact-methods">
            <div className="contact-method">
              <div className="contact-icon">
                <Globe className="icon" />
              </div>
              <div className="contact-info">
                <h4>Contact from Our Website</h4>
                <p>Get in touch through our main contact page</p>
                <a 
                  href="https://www.greenbeatceylon.com/#contact" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="contact-link"
                >
                  greenbeatceylon.com/contact <ArrowRight className="link-icon" />
                </a>
              </div>
            </div>

            <div className="contact-method">
              <div className="contact-icon">
                <Send className="icon" />
              </div>
              <div className="contact-info">
                <h4>Email Us Directly</h4>
                <p>Reach out for investment opportunities and partnerships</p>
                <a 
                  href="mailto:Info@gbceylon.com?subject=Investment Inquiry - Green Beat Ceylon"
                  className="contact-link"
                >
                  Info@gbceylon.com <ArrowRight className="link-icon" />
                </a>
              </div>
            </div>
          </div>

          <div className="contact-cta">
            <p className="cta-text">
              <strong>Ready to invest in Sri Lanka's agricultural future?</strong> Contact us today to discuss how we can grow together.
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <div className="navigation-arrows">
        <button
          className={`nav-arrow back-to-top ${showBackToTop ? "visible" : ""}`}
          onClick={scrollToTop}
          data-tooltip="Back to Top"
          aria-label="Back to top"
        >
          <ArrowUp />
        </button>
        <button
          className="nav-arrow back-to-home"
          onClick={goToHome}
          data-tooltip="Back to Home"
          aria-label="Back to home"
        >
          <Home />
        </button>
      </div>

      {/* Background Elements */}
      <div className="bg-leaf bg-leaf-1"></div>
      <div className="bg-leaf bg-leaf-2"></div>
    </section>
  )
}
